import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    TablePagination
} from "@mui/material";
import supabase from "../../services/supabaseClient";

const ORDER_OPTIONS = [
    { value: "contact_date_desc", label: "Contact Date (Newest)" },
    { value: "contact_date_asc", label: "Contact Date (Oldest)" },
    { value: "name_asc", label: "Bar Name (A-Z)" },
    { value: "city_asc", label: "City (A-Z)" }
];

const compareStrings = (a = "", b = "") =>
    a.localeCompare(b, undefined, { sensitivity: "base" });

const parseCityFromAddress = (address) => {
    if (!address || typeof address !== "string") {
        return "";
    }

    const segments = address
        .split(",")
        .map((segment) => segment.trim())
        .filter(Boolean);

    if (segments.length === 0) {
        return "";
    }
    if (segments.length === 1) {
        return segments[0];
    }
    if (segments.length === 2) {
        return segments[0];
    }

    return segments[segments.length - 2] || "";
};

const getDateValue = (row) => {
    if (row.dateInput) {
        const parsed = Date.parse(`${row.dateInput}T00:00:00Z`);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    if (row.contacted_by_agent_date) {
        const parsed = Date.parse(row.contacted_by_agent_date);
        if (!Number.isNaN(parsed)) {
            return parsed;
        }
    }

    return null;
};

function normalizeRows(data = []) {
    return data.map((row) => {
        const bar = row.bars || {};
        const currentDate = row.contacted_by_agent_date;
        const barAddress = bar.bar_address || "";
        return {
            bar_id: row.bar_id,
            bar_name: bar.bar_name || "Unknown bar",
            bar_address: barAddress,
            city: parseCityFromAddress(barAddress),
            contacted_by_agent: Boolean(row.contacted_by_agent),
            contacted_by_agent_name: row.contacted_by_agent_name || "",
            dateInput: currentDate ? currentDate.slice(0, 10) : "",
            contacted_by_agent_date: currentDate || null,
            contact_notes: row.contact_notes || ""
        };
    });
}

export default function ContactedBars() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [savingById, setSavingById] = useState({});
    const [sortOption, setSortOption] = useState("contact_date_desc");

    const [searchTerm, setSearchTerm] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const filteredRows = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) {
            return rows;
        }

        return rows.filter((row) => {
            return (
                (row.bar_name && row.bar_name.toLowerCase().includes(term)) ||
                (row.city && row.city.toLowerCase().includes(term)) ||
                (row.bar_address && row.bar_address.toLowerCase().includes(term)) ||
                (row.contacted_by_agent_name &&
                    row.contacted_by_agent_name.toLowerCase().includes(term)) ||
                (row.contact_notes && row.contact_notes.toLowerCase().includes(term))
            );
        });
    }, [rows, searchTerm]);

    const sortedRows = useMemo(() => {
        const copy = [...filteredRows];

        switch (sortOption) {
            case "name_asc":
                copy.sort((a, b) => compareStrings(a.bar_name, b.bar_name));
                break;
            case "city_asc":
                copy.sort((a, b) => {
                    const cityComparison = compareStrings(a.city, b.city);
                    if (cityComparison !== 0) {
                        return cityComparison;
                    }
                    return compareStrings(a.bar_name, b.bar_name);
                });
                break;
            case "contact_date_asc":
                copy.sort((a, b) => {
                    const aValue = getDateValue(a);
                    const bValue = getDateValue(b);

                    if (aValue === null && bValue === null) {
                        return 0;
                    }
                    if (aValue === null) {
                        return 1;
                    }
                    if (bValue === null) {
                        return -1;
                    }
                    return aValue - bValue;
                });
                break;
            case "contact_date_desc":
            default:
                copy.sort((a, b) => {
                    const aValue = getDateValue(a);
                    const bValue = getDateValue(b);

                    if (aValue === null && bValue === null) {
                        return 0;
                    }
                    if (aValue === null) {
                        return 1;
                    }
                    if (bValue === null) {
                        return -1;
                    }
                    return bValue - aValue;
                });
                break;
        }

        return copy;
    }, [filteredRows, sortOption]);

    const hasRows = sortedRows.length > 0;

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError("");
        setSuccess("");
        const { data, error: fetchError } = await supabase
            .from("reviewed_and_contacted_bars")
            .select("bar_id, contacted_by_agent, contacted_by_agent_date, contacted_by_agent_name, contact_notes, bars(bar_name, bar_address)")
            .order("contacted_by_agent", { ascending: true })
            .order("contacted_by_agent_date", { ascending: false, nullsFirst: false });

        if (fetchError) {
            setError(fetchError.message || "Unable to load contacted bars right now.");
            setRows([]);
        } else {
            setRows(normalizeRows(data || []));
            setPage(0);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [sortedRows.length, rowsPerPage, page]);

    const updateLocalRow = (barId, updates) => {
        setRows((prev) =>
            prev.map((row) =>
                row.bar_id === barId
                    ? {
                          ...row,
                          ...updates
                      }
                    : row
            )
        );
    };

    const handleFieldChange = (barId, field, value) => {
        updateLocalRow(barId, { [field]: value });
        setSuccess("");
    };

    const handleToggleContacted = (barId) => {
        setRows((prev) =>
            prev.map((row) =>
                row.bar_id === barId
                    ? {
                          ...row,
                          contacted_by_agent: !row.contacted_by_agent
                      }
                    : row
            )
        );
        setSuccess("");
    };

    const handleSave = async (row) => {
        setSavingById((prev) => ({ ...prev, [row.bar_id]: true }));
        setError("");
        setSuccess("");

        const trimmedName = row.contacted_by_agent_name.trim();
        const trimmedNotes = row.contact_notes.trim();
        const dateIso = row.dateInput ? new Date(`${row.dateInput}T00:00:00.000Z`).toISOString() : null;

        const { error: updateError } = await supabase
            .from("reviewed_and_contacted_bars")
            .update({
                contacted_by_agent: row.contacted_by_agent,
                contacted_by_agent_name: trimmedName || null,
                contacted_by_agent_date: dateIso,
                contact_notes: trimmedNotes || null
            })
            .eq("bar_id", row.bar_id);

        if (updateError) {
            setError(updateError.message || "Unable to update this record. Please try again.");
        } else {
            updateLocalRow(row.bar_id, {
                contacted_by_agent_name: trimmedName,
                contacted_by_agent_date: dateIso,
                dateInput: row.dateInput,
                contact_notes: trimmedNotes
            });
            setSuccess("Contact details saved.");
        }

        setSavingById((prev) => {
            const next = { ...prev };
            delete next[row.bar_id];
            return next;
        });
    };

    return (
        <Container>
            <Header>
                <span></span>  {/*To push content to the right*/}
                <Box display="flex" alignItems="center" gap={2}>
                    <TextField
                        type="search"
                        size="small"
                        placeholder="Search bars"
                        value={searchTerm}
                        onChange={(event) => {
                            setSearchTerm(event.target.value);
                            setPage(0);
                        }}
                        sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small">
                        <InputLabel id="contacted-bars-order-label">Order By</InputLabel>
                        <Select
                            labelId="contacted-bars-order-label"
                            id="contacted-bars-order"
                            value={sortOption}
                            label="Order By"
                            onChange={(event) => setSortOption(event.target.value)}
                        >
                            {ORDER_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={loadRows} disabled={loading}>
                        Refresh
                    </Button>
                </Box>
            </Header>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <Box display="flex" justifyContent="center" mt={4}>
                    <CircularProgress />
                </Box>
            ) : !hasRows ? (
                <Typography variant="body1">No contacted bars found.</Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Bar</TableCell>
                                <TableCell>Contacted?</TableCell>
                                <TableCell>Sales Person</TableCell>
                                <TableCell>Contact Date</TableCell>
                                <TableCell>Notes</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedRows.map((row) => {
                                const isSaving = Boolean(savingById[row.bar_id]);
                                return (
                                    <TableRow key={row.bar_id} hover>
                                        <TableCell>
                                            <strong>{row.bar_name}</strong>
                                            {row.bar_address && (
                                                <div className="muted">{row.bar_address}</div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Checkbox
                                                color="primary"
                                                checked={row.contacted_by_agent}
                                                onChange={() => handleToggleContacted(row.bar_id)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.contacted_by_agent_name}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "contacted_by_agent_name",
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Name"
                                                size="small"
                                                fullWidth
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                type="date"
                                                value={row.dateInput}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "dateInput",
                                                        event.target.value
                                                    )
                                                }
                                                size="small"
                                                fullWidth
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <TextField
                                                value={row.contact_notes}
                                                onChange={(event) =>
                                                    handleFieldChange(
                                                        row.bar_id,
                                                        "contact_notes",
                                                        event.target.value
                                                    )
                                                }
                                                placeholder="Add notes"
                                                size="small"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                onClick={() => handleSave(row)}
                                                disabled={isSaving}
                                            >
                                                {isSaving ? "Saving..." : "Save"}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={sortedRows.length}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(Number(event.target.value));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 250]}
                        labelRowsPerPage="Rows per page"
                    />
                </TableContainer>
            )}
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;

    .muted {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.6);
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;
